import classNames from "classnames";
import { FC, ReactNode, useEffect, useState } from "react";
import Colors from "~/constants/colors";
import Icon from "../Icon";
import Text, { TextProps } from "../Text";
import styles from "./Accordion.module.scss";
import { IconNames } from "../Icon/Icon.type";
import AccordionContent from "./AccordionContent";

interface AccordionProps {
  items: {
    label?: ReactNode;
    key: string;
    labelType?: TextProps["type"];
    labelColor?: string;
    content: React.ReactNode;
    icon?: IconNames;
    extra?: ReactNode;
  }[];
  className?: string;
  variant?: "filled";
  defaultActive?: string[];
  labelClassName?: string;
  itemClassName?: string;
  usingLayoutV2?: boolean;
}

const Accordion: FC<AccordionProps> = ({
  items = [],
  className,
  variant,
  defaultActive,
  labelClassName,
  itemClassName,
  usingLayoutV2 = false,
}) => {
  const [activeLabels, setActiveLabels] = useState(defaultActive || []);

  const handleChangeLabel = (label: string) => {
    const isExist = activeLabels.includes(label);

    if (isExist) {
      setActiveLabels(activeLabels.filter((item) => item !== label));
    } else {
      setActiveLabels([...activeLabels, label]);
    }
  };

  return (
    <div
      className={classNames(styles.accordion, className, {
        [styles.filled]: variant === "filled",
        "outline outline-solid outline-[#878787] outline-1": usingLayoutV2,
      })}
      style={{
        gap: usingLayoutV2 ? 0 : "0.5rem",
      }}
    >
      {items.map((item) => {
        const isOpen = activeLabels.includes(item.key);
        return (
          <div
            key={item.key}
            className={classNames(styles.accordion_item, itemClassName, {
              [styles.open]: isOpen,
            })}
            style={{
              overflow: isOpen ? "visible" : "hidden",
              gap: usingLayoutV2 ? 0 : "0.5rem",
              paddingBottom: usingLayoutV2 ? 0 : "0.5rem",
              // outline: usingLayoutV2 ? "1px solid #878787" : "none",
            }}
          >
            <div
              className={classNames(styles.accordion_label, labelClassName, {
                "p-4 outline outline-solid outline-[#878787] outline-1":
                  usingLayoutV2,
              })}
              onClick={() => handleChangeLabel(item.key)}
            >
              <div className="flex items-center gap-2">
                {item.icon && (
                  <div className={styles.icon_wrapper}>
                    <Icon name={item.icon} />
                  </div>
                )}
                {typeof item.label === "string" ? (
                  <Text
                    type={item.labelType || "title-4"}
                    color={item.labelColor}
                    className={classNames("font-semibold", {
                      uppercase: usingLayoutV2,
                    })}
                  >
                    {item.label}
                  </Text>
                ) : (
                  item.label
                )}
              </div>
              <div className="flex items-center gap-4">
                {item?.extra && (
                  <div className="text-right inline-flex">{item?.extra}</div>
                )}
                <Icon
                  className={styles.icon}
                  name="arrow-square-down-2"
                  color={Colors.GREY_900}
                />
              </div>
            </div>
            {item?.content && <AccordionContent content={item?.content} />}
          </div>
        );
      })}
    </div>
  );
};

export default Accordion;
